import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, FileText, Plus, X, Send, CheckCircle2,
  AlertTriangle, Clock, Sparkles, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { verificationsApi } from "@/api/verifications";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import {
  DEFAULT_PUBLIC_BADGE,
  PUBLIC_BADGE_CONSENT_LABEL,
  PUBLIC_BADGE_CONSENT_HELP,
  postVerdictPath,
} from "@/lib/verdictBadge";

/**
 * ExpertReviewPage — /expert/review/:verificationId
 *
 * The annotation surface the merge strategy calls out as the largest
 * V2 build. Split view:
 *
 *   Left  | the AI output the client submitted + brief context
 *   Right | the verdict form (verdict_text, confidence, findings, items)
 *
 * Submits to POST /api/verifications/:id/verdict which auto-creates the
 * verification_badges row. The expert can save a draft (re-submit later)
 * — server-side the endpoint is idempotent and updates the existing
 * verdict.
 *
 * Visual rhythm follows the Clean Enterprise system:
 *   canvas background, white cards, ink text, indigo accents, gold CTA.
 */

const CONFIDENCE = [
  { key: "high",   label: "High",   tone: "emerald", desc: "I'd sign my name on this. No material reservations." },
  { key: "medium", label: "Medium", tone: "amber",   desc: "Mostly correct with caveats — see findings." },
  { key: "low",    label: "Low",    tone: "rose",    desc: "Material issues. Don't act on this without changes." },
];

export default function ExpertReviewPage() {
  const { verificationId } = useParams();
  const navigate           = useNavigate();

  const [brief, setBrief]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);

  // Verdict form state
  const [verdictText,        setVerdictText]        = useState("");
  const [verdictSummary,     setVerdictSummary]     = useState("");
  const [confidenceLevel,    setConfidenceLevel]    = useState("medium");
  const [keyFindings,        setKeyFindings]        = useState([""]);
  const [actionItems,        setActionItems]        = useState([""]);
  const [followUpRecommended,setFollowUpRecommended]= useState(false);
  // Private unless the expert deliberately publishes (with the buyer's OK).
  const [isPublicBadge,      setIsPublicBadge]      = useState(DEFAULT_PUBLIC_BADGE);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    verificationsApi.getBrief(verificationId)
      .then((data) => {
        if (!mounted) return;
        setBrief(data);

        // Pre-fill the form if a verdict already exists (re-edit flow).
        // Note: the GET /brief endpoint only tells us verdict_submitted/verdict_id;
        // we don't have the full verdict text yet via this endpoint. That's
        // intentional — the verdict text is the expert's own draft and re-fetch
        // would clobber unsaved edits.
        if (data?.context_package?.user_goal && !verdictSummary) {
          // Suggest a starter summary based on category.
          const label = data?.category_label || data?.category || "review";
          setVerdictSummary(`Reviewed ${label} submission. `);
        }
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.response?.status === 404
          ? "This verification doesn't exist or isn't assigned to you."
          : err?.response?.status === 403
            ? "You don't have access to this review."
            : "Couldn't load the brief.");
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationId]);

  const canSubmit = useMemo(() => {
    return verdictText.trim().length >= 10 && !!confidenceLevel;
  }, [verdictText, confidenceLevel]);

  // ── List helpers ────────────────────────────────────────────────────────
  const setListItem = (list, set) => (idx, value) => {
    const next = [...list];
    next[idx] = value;
    set(next);
  };
  const addListItem = (list, set) => () => set([...list, ""]);
  const removeListItem = (list, set) => (idx) => {
    const next = list.filter((_, i) => i !== idx);
    set(next.length ? next : [""]);
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Verdict needs at least 10 characters and a confidence level.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        verdict_text:           verdictText.trim(),
        verdict_summary:        verdictSummary.trim() || null,
        confidence_level:       confidenceLevel,
        key_findings:           keyFindings.map((s) => s.trim()).filter(Boolean),
        action_items:           actionItems.map((s) => s.trim()).filter(Boolean),
        follow_up_recommended:  followUpRecommended,
        is_public_badge:        isPublicBadge,
      };
      const result = await verificationsApi.submitVerdict(verificationId, payload);
      toast.success(`Verdict ${result.action === "created" ? "submitted" : "updated"}.`);
      // A public badge lands on its public page; a private one has no public
      // page (it would 404), so it goes back to the dashboard.
      navigate(postVerdictPath(result));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to submit verdict";
      toast.error(typeof detail === "string" ? detail : "Failed to submit verdict");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !brief) {
    return (
      <div className="min-h-screen bg-canvas text-ink font-body flex flex-col">
        <MarketingNav />
        <div className="flex-1 flex items-center justify-center px-4 text-center">
          <div className="max-w-md">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl text-ink">Can't open this review</h1>
            <p className="text-slateline-600 mt-3">{error || "Try again later."}</p>
            <Link to="/expert-dashboard"
                  className="mt-6 inline-flex items-center gap-1.5 text-brand-green hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to dashboard
            </Link>
          </div>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  const ctx = brief.context_package;
  const dueAt = brief.due_at ? new Date(brief.due_at) : null;
  const dueHuman = dueAt
    ? dueAt.toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : null;

  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />

      {/* Header strip */}
      <section className="border-b border-slateline-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <Link to="/expert-dashboard"
                  className="inline-flex items-center gap-1.5 text-sm text-slateline-500 hover:text-ink">
              <ArrowLeft className="w-3.5 h-3.5" /> Expert dashboard
            </Link>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl text-ink leading-tight">
                {brief.artifact_name || "Verification review"}
              </h1>
              <StatusPill status={brief.status} />
            </div>
            <div className="mt-2 text-sm text-slateline-500 flex items-center gap-4 flex-wrap">
              {brief.category_label && (
                <span className="inline-flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  {brief.category_label}
                </span>
              )}
              {brief.source_platform && brief.source_platform !== "direct" && (
                <span className="inline-flex items-center gap-1.5 capitalize">
                  <Sparkles className="w-3.5 h-3.5 text-brand-green" />
                  via {brief.source_platform}
                </span>
              )}
              {dueHuman && (
                <span className={`inline-flex items-center gap-1.5 ${
                  dueAt && dueAt < new Date() ? "text-rose-600 font-semibold" : ""
                }`}>
                  <Clock className="w-3.5 h-3.5" />
                  Due {dueHuman}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Split view */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 grid lg:grid-cols-[1fr,1fr] gap-6">
        {/* Left — AI submission + context */}
        <SubmissionPane brief={brief} ctx={ctx} />

        {/* Right — verdict form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Confidence */}
          <Card>
            <CardLabel>1 · Confidence level</CardLabel>
            <div className="grid grid-cols-3 gap-2">
              {CONFIDENCE.map((c) => {
                const active = confidenceLevel === c.key;
                return (
                  <button
                    key={c.key} type="button"
                    onClick={() => setConfidenceLevel(c.key)}
                    className={[
                      "px-3 py-3 rounded-md border text-left transition-all",
                      active
                        ? `border-${c.tone}-500 bg-${c.tone}-50 ring-1 ring-${c.tone}-200`
                        : "border-slateline-200 hover:border-ink/30",
                    ].join(" ")}
                  >
                    <div className={`text-xs font-bold uppercase tracking-wider ${
                      active ? `text-${c.tone}-700` : "text-slateline-500"
                    }`}>
                      {c.label}
                    </div>
                    <div className={`text-xs mt-1 leading-snug ${
                      active ? "text-ink" : "text-slateline-600"
                    }`}>
                      {c.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Verdict text */}
          <Card>
            <CardLabel>2 · Written verdict *</CardLabel>
            <textarea
              value={verdictText}
              onChange={(e) => setVerdictText(e.target.value)}
              rows={8}
              maxLength={10000}
              placeholder="Walk the client through your judgment. What's right, what's wrong, what to do about it. Plain language — this is what they pay for."
              className="w-full bg-white border border-slateline-200 rounded-md px-3 py-3
                         text-ink placeholder-slateline-400 outline-none
                         focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors
                         text-sm leading-relaxed"
            />
            <div className="mt-1.5 flex items-center justify-between text-xs">
              <span className={verdictText.trim().length < 10
                ? "text-rose-600 font-medium"
                : "text-slateline-400"}>
                {verdictText.trim().length < 10
                  ? `${10 - verdictText.trim().length} more characters needed`
                  : `${verdictText.trim().length.toLocaleString()} chars`}
              </span>
              <span className="text-slateline-400">Min. 10 · Max 10,000</span>
            </div>
          </Card>

          {/* Public summary */}
          <Card>
            <CardLabel>
              3 · Public badge summary
              <span className="ml-2 text-slateline-400 font-normal normal-case">(280 char max)</span>
            </CardLabel>
            <input
              type="text"
              value={verdictSummary}
              onChange={(e) => setVerdictSummary(e.target.value)}
              maxLength={280}
              placeholder="One-liner shown on the public /verified/{slug} page."
              className="w-full bg-white border border-slateline-200 rounded-md px-3 h-11
                         text-ink placeholder-slateline-400 outline-none
                         focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors text-sm"
            />
            <div className="mt-1.5 text-xs text-slateline-400 text-right">
              {verdictSummary.length} / 280
            </div>
          </Card>

          {/* Key findings */}
          <Card>
            <CardLabel>4 · Key findings</CardLabel>
            <ListInput
              items={keyFindings}
              placeholder="e.g. Clause 8.2 is unenforceable under NY law."
              onChange={setListItem(keyFindings, setKeyFindings)}
              onAdd={addListItem(keyFindings, setKeyFindings)}
              onRemove={removeListItem(keyFindings, setKeyFindings)}
            />
          </Card>

          {/* Action items */}
          <Card>
            <CardLabel>5 · Action items</CardLabel>
            <ListInput
              items={actionItems}
              placeholder="e.g. Replace clause 8.2 with our redline."
              onChange={setListItem(actionItems, setActionItems)}
              onAdd={addListItem(actionItems, setActionItems)}
              onRemove={removeListItem(actionItems, setActionItems)}
            />
          </Card>

          {/* Flags */}
          <Card>
            <CardLabel>6 · Flags</CardLabel>
            <div className="space-y-3">
              <ToggleRow
                checked={followUpRecommended}
                onChange={setFollowUpRecommended}
                title="Recommend a follow-up review"
                description="Tells the client a further written review of a revised version would help."
                icon={Sparkles}
              />
              <ToggleRow
                checked={isPublicBadge}
                onChange={setIsPublicBadge}
                title={PUBLIC_BADGE_CONSENT_LABEL}
                description={PUBLIC_BADGE_CONSENT_HELP}
                icon={ShieldCheck}
              />
            </div>
          </Card>

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit" disabled={!canSubmit || submitting}
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md
                         bg-brand-green hover:bg-brand-greenHover disabled:opacity-50 disabled:cursor-not-allowed
                         text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {brief.verdict_submitted ? "Update verdict & re-issue badge" : "Submit verdict & issue badge"}
                </>
              )}
            </button>
            <p className="text-xs text-slateline-500 mt-3 text-center">
              Submitting issues an Xpert Verified badge, private unless you
              publish it above. You can update the verdict later; the badge
              slug stays the same, and the public setting is whatever you
              choose on that submit.
            </p>
          </div>
        </form>
      </main>

      <MarketingFooter />
    </div>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

function SubmissionPane({ brief, ctx }) {
  return (
    <div className="space-y-5">
      {/* Brief context */}
      {ctx && (
        <Card>
          <CardLabel>Client brief</CardLabel>
          {ctx.user_goal && (
            <div className="mb-4 p-4 rounded-md bg-brand-green/5 border border-brand-green/20">
              <div className="text-[11px] font-bold uppercase tracking-wider text-brand-green mb-1">
                What they want verified
              </div>
              <p className="text-sm text-ink leading-relaxed">{ctx.user_goal}</p>
            </div>
          )}
          {brief.review_instructions && (
            <div className="mb-4 text-sm text-slateline-700 leading-relaxed">
              <span className="font-semibold">Specific instructions:</span>{" "}
              {brief.review_instructions}
            </div>
          )}
          {ctx.conversation_summary && (
            <div className="text-sm text-slateline-700 leading-relaxed">
              <span className="font-semibold">AI pre-analysis:</span>{" "}
              {ctx.conversation_summary}
            </div>
          )}
          {ctx.key_questions?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slateline-500 mb-2">
                Key questions the AI couldn't resolve
              </div>
              <ul className="space-y-1 text-sm text-slateline-700">
                {ctx.key_questions.map((q, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-slateline-400">→</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* The AI output itself */}
      <Card>
        <CardLabel className="flex items-center justify-between">
          <span>AI output to review</span>
          {brief.source_platform && brief.source_platform !== "direct" && (
            <span className="text-[11px] capitalize font-bold tracking-wider text-brand-green bg-brand-green/10
                             border border-brand-green/20 px-2 py-0.5 rounded-full">
              {brief.source_platform}
            </span>
          )}
        </CardLabel>
        <pre className="bg-canvas border border-slateline-200 rounded-md p-4 text-sm text-ink
                        whitespace-pre-wrap font-mono leading-relaxed max-h-[640px] overflow-auto">
          {brief.submission_text || "[no submission text available]"}
        </pre>
        {brief.submission_source_url && (
          <a href={brief.submission_source_url}
             target="_blank" rel="noopener noreferrer"
             className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-green hover:underline">
            Open source link
            <span aria-hidden="true">→</span>
          </a>
        )}
      </Card>
    </div>
  );
}

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white border border-slateline-200 rounded-xl p-5 sm:p-6 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function CardLabel({ children, className = "" }) {
  return (
    <div className={`text-xs font-bold uppercase tracking-[0.16em] text-slateline-500 mb-3 ${className}`}>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const tone =
    status === "completed" ? { bg: "bg-brand-greenTint", border: "border-brand-green/30", text: "text-brand-green", label: "Completed" } :
    status === "in_review" ? { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   label: "In review" } :
    status === "assigned"  ? { bg: "bg-brand-green/10", border: "border-brand-green/30", text: "text-brand-green", label: "Assigned" } :
    status === "cancelled" ? { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-700",    label: "Cancelled" } :
                             { bg: "bg-slateline-100", border: "border-slateline-200", text: "text-slateline-600", label: status };
  return (
    <span className={`inline-flex items-center text-[11px] font-bold tracking-wider uppercase rounded-full px-2 py-0.5 border ${tone.bg} ${tone.border} ${tone.text}`}>
      {tone.label}
    </span>
  );
}

function ListInput({ items, placeholder, onChange, onAdd, onRemove }) {
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text" value={item}
            onChange={(e) => onChange(idx, e.target.value)}
            placeholder={placeholder}
            className="flex-1 h-10 bg-white border border-slateline-200 rounded-md px-3
                       text-ink text-sm placeholder-slateline-400 outline-none
                       focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
          />
          <button type="button"
                  onClick={() => onRemove(idx)}
                  disabled={items.length === 1 && !items[0]}
                  className="p-2 text-slateline-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={onAdd}
              className="inline-flex items-center gap-1.5 text-sm text-brand-green hover:text-brand-greenHover font-medium">
        <Plus className="w-3.5 h-3.5" />
        Add another
      </button>
    </div>
  );
}

function ToggleRow({ checked, onChange, title, description, icon: Icon }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-md hover:bg-canvas cursor-pointer transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slateline-300 text-brand-green focus:ring-brand-green/30"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink inline-flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5 text-brand-green" />}
          {title}
        </div>
        <div className="text-xs text-slateline-500 mt-0.5 leading-relaxed">
          {description}
        </div>
      </div>
    </label>
  );
}
